import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { identificarClientePublico } from '../api/clientesPublico';
import { PedidoPublicoIdentificacao } from '../api/pedidos';
import { ClientePublico, ClientePublicoEndereco } from '../types';
import { formatarCelular, formatarCep } from '../utils/mascaras';

interface Props {
  show: boolean;
  tenantId: string;
  telefoneSalvo?: string;
  enviando?: boolean;
  onConfirm: (identificacao: PedidoPublicoIdentificacao) => void;
  onHide: () => void;
}

interface EnderecoForm {
  cep: string;
  logradouro: string;
  numero_endereco: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
}

const ENDERECO_INICIAL: EnderecoForm = {
  cep: '',
  logradouro: '',
  numero_endereco: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

async function consultarCep(cep: string) {
  const resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
  const dados = await resposta.json();

  if (!resposta.ok || dados.erro) {
    throw new Error('CEP nao encontrado.');
  }

  return {
    logradouro: dados.logradouro || '',
    bairro: dados.bairro || '',
    cidade: dados.localidade || '',
    uf: dados.uf || '',
  };
}

export function IdentificacaoClienteModal({ show, tenantId, telefoneSalvo, enviando, onConfirm, onHide }: Props) {
  const [etapa, setEtapa] = useState<'telefone' | 'dados'>('telefone');
  const [telefone, setTelefone] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [clienteEncontrado, setClienteEncontrado] = useState<ClientePublico | null>(null);
  const [nome, setNome] = useState('');
  const [enderecoSalvo, setEnderecoSalvo] = useState<ClientePublicoEndereco | null>(null);
  const [usarNovoEndereco, setUsarNovoEndereco] = useState(false);
  const [endereco, setEndereco] = useState<EnderecoForm>(ENDERECO_INICIAL);
  const [consultandoCep, setConsultandoCep] = useState(false);
  const [erroCep, setErroCep] = useState<string | null>(null);
  const ultimoCepRef = useRef('');

  useEffect(() => {
    if (!show) return;
    setEtapa('telefone');
    setTelefone(telefoneSalvo ? formatarCelular(telefoneSalvo) : '');
    setErro(null);
    setClienteEncontrado(null);
    setNome('');
    setEnderecoSalvo(null);
    setUsarNovoEndereco(false);
    setEndereco(ENDERECO_INICIAL);
    setErroCep(null);
    ultimoCepRef.current = '';

    if (telefoneSalvo) {
      identificarPorTelefone(telefoneSalvo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  async function identificarPorTelefone(valor: string) {
    const digitos = valor.replace(/\D/g, '');
    if (digitos.length < 10 || digitos.length > 11) {
      setErro('Informe um numero de telefone valido, com DDD.');
      return;
    }

    setErro(null);
    setVerificando(true);
    try {
      const cliente = await identificarClientePublico(tenantId, digitos);
      if (cliente) {
        setClienteEncontrado(cliente);
        setNome(cliente.nome);
        setEnderecoSalvo(cliente.enderecos[0] ?? null);
        setUsarNovoEndereco(cliente.enderecos.length === 0);
      } else {
        setClienteEncontrado(null);
        setNome('');
        setEnderecoSalvo(null);
        setUsarNovoEndereco(true);
      }
      setEtapa('dados');
    } catch (error: any) {
      setErro(error.response?.data?.erro || 'Nao foi possivel verificar o telefone. Tente novamente.');
    } finally {
      setVerificando(false);
    }
  }

  useEffect(() => {
    const cep = endereco.cep.replace(/\D/g, '');
    if (cep.length !== 8 || cep === ultimoCepRef.current) return;

    ultimoCepRef.current = cep;
    setConsultandoCep(true);
    setErroCep(null);

    consultarCep(cep)
      .then((dados) => {
        setEndereco((atual) => ({ ...atual, logradouro: dados.logradouro, bairro: dados.bairro, cidade: dados.cidade, uf: dados.uf }));
      })
      .catch((error: Error) => setErroCep(error.message))
      .finally(() => setConsultandoCep(false));
  }, [endereco.cep]);

  function handleContinuar() {
    void identificarPorTelefone(telefone);
  }

  function handleConfirmar() {
    if (!nome.trim()) {
      setErro('Informe seu nome.');
      return;
    }

    const usandoEnderecoSalvo = enderecoSalvo && !usarNovoEndereco;
    if (!usandoEnderecoSalvo) {
      if (!endereco.cep || !endereco.numero_endereco || !endereco.logradouro || !endereco.bairro || !endereco.cidade || !endereco.uf) {
        setErro('Preencha o CEP e o numero do endereco.');
        return;
      }
    }

    setErro(null);
    onConfirm({
      telefone: telefone.replace(/\D/g, ''),
      nome: nome.trim(),
      endereco: usandoEnderecoSalvo
        ? {
            cep: enderecoSalvo!.cep,
            logradouro: enderecoSalvo!.logradouro,
            numero_endereco: enderecoSalvo!.numero,
            complemento: enderecoSalvo!.complemento || undefined,
            bairro: enderecoSalvo!.bairro,
            cidade: enderecoSalvo!.cidade,
            uf: enderecoSalvo!.uf,
          }
        : {
            cep: endereco.cep,
            logradouro: endereco.logradouro,
            numero_endereco: endereco.numero_endereco,
            complemento: endereco.complemento || undefined,
            bairro: endereco.bairro,
            cidade: endereco.cidade,
            uf: endereco.uf,
          },
    });
  }

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="lizzi-public-modal" contentClassName="lizzi-public-modal-content">
      <Modal.Header closeButton>
        <Modal.Title>Finalizar pedido</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {etapa === 'telefone' && (
          <>
            <p className="lizzi-muted-center" style={{ margin: '0 0 14px' }}>
              Informe seu telefone pra gente te reconhecer nos proximos pedidos.
            </p>
            <label className="lizzi-public-modal-field">
              <span>Telefone celular</span>
              <input
                className="lizzi-public-modal-input"
                value={telefone}
                onChange={(e) => setTelefone(formatarCelular(e.target.value))}
                placeholder="(11) 91234-5678"
                inputMode="numeric"
                autoFocus
              />
            </label>
            {erro && <div className="lizzi-public-modal-error">{erro}</div>}
            <button
              type="button"
              className="lizzi-public-primary-button"
              style={{ marginTop: '16px' }}
              onClick={handleContinuar}
              disabled={verificando}
            >
              {verificando ? 'Verificando...' : 'Continuar'}
            </button>
          </>
        )}

        {etapa === 'dados' && (
          <>
            {clienteEncontrado && <p className="lizzi-muted-center" style={{ margin: '0 0 14px' }}>Ola de novo, {clienteEncontrado.nome.split(' ')[0]}!</p>}

            <label className="lizzi-public-modal-field">
              <span>Nome</span>
              <input className="lizzi-public-modal-input" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome" />
            </label>

            {enderecoSalvo && !usarNovoEndereco ? (
              <div className="lizzi-public-modal-field">
                <span>Endereco de entrega</span>
                <div className="lizzi-public-modal-endereco-salvo">
                  {enderecoSalvo.logradouro}, {enderecoSalvo.numero}
                  {enderecoSalvo.complemento ? ` - ${enderecoSalvo.complemento}` : ''} — {enderecoSalvo.bairro}, {enderecoSalvo.cidade}/{enderecoSalvo.uf}
                </div>
                <button type="button" className="lizzi-link-button" onClick={() => setUsarNovoEndereco(true)}>
                  Usar outro endereco
                </button>
              </div>
            ) : (
              <>
                <div className="lizzi-form-grid-2">
                  <label className="lizzi-public-modal-field">
                    <span>CEP</span>
                    <input
                      className="lizzi-public-modal-input"
                      value={endereco.cep}
                      onChange={(e) => setEndereco((atual) => ({ ...atual, cep: formatarCep(e.target.value) }))}
                      placeholder="00000-000"
                      inputMode="numeric"
                    />
                  </label>
                  <label className="lizzi-public-modal-field">
                    <span>Numero</span>
                    <input
                      className="lizzi-public-modal-input"
                      value={endereco.numero_endereco}
                      onChange={(e) => setEndereco((atual) => ({ ...atual, numero_endereco: e.target.value }))}
                      placeholder="Numero"
                    />
                  </label>
                </div>
                {consultandoCep && <p className="lizzi-muted-center">Buscando endereco...</p>}
                {erroCep && <div className="lizzi-public-modal-error">{erroCep}</div>}
                {endereco.logradouro && (
                  <p className="lizzi-muted-center" style={{ textAlign: 'left' }}>
                    {endereco.logradouro}, {endereco.bairro} — {endereco.cidade}/{endereco.uf}
                  </p>
                )}
                <label className="lizzi-public-modal-field">
                  <span>Complemento (opcional)</span>
                  <input
                    className="lizzi-public-modal-input"
                    value={endereco.complemento}
                    onChange={(e) => setEndereco((atual) => ({ ...atual, complemento: e.target.value }))}
                    placeholder="Apto, bloco, referencia..."
                  />
                </label>
                {enderecoSalvo && (
                  <button type="button" className="lizzi-link-button" onClick={() => setUsarNovoEndereco(false)}>
                    Usar o endereco salvo
                  </button>
                )}
              </>
            )}

            {erro && <div className="lizzi-public-modal-error">{erro}</div>}
            <button
              type="button"
              className="lizzi-public-primary-button"
              style={{ marginTop: '16px' }}
              onClick={handleConfirmar}
              disabled={enviando}
            >
              {enviando ? 'Enviando...' : 'Confirmar e enviar pedido'}
            </button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
